import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormControl,
  FormGroup,
  ValidatorFn
} from '@angular/forms';

/**
 * Recursively creates a deep clone of of a provided object;
 *
 * @param formData The form data to be cloned.
 */
export function deepCloneFormData<T = AbstractControl>(formData: T): T {
  if (formData instanceof FormArray) {
    return deepCloneFormArray(formData) as typeof formData;
  } else if (formData instanceof FormGroup) {
    return deepCloneFormGroup(formData) as typeof formData;
  } else if (formData instanceof FormControl) {
    return deepCloneFormControl(formData) as typeof formData;
  }

  throw new Error('Unknown object type provided.');
}

/**
 * Recursivly clones a {@type FormGroup} object.
 *
 * @param formGroup The form group to be cloned.
 */
function deepCloneFormGroup(formGroup: FormGroup): FormGroup {
  const clonedGroup: FormGroup = new FormGroup({});

  Object.keys(formGroup.controls).forEach((key: string): void => {
    const control: AbstractControl = formGroup.get(key);
    clonedGroup.addControl(key, deepCloneFormData(control));
  });

  return clonedGroup;
}

/**
 * Recursivly clones a {@type FormArray} object.
 *
 * @param formGroup The form array to be cloned.
 */
function deepCloneFormArray(formArray: FormArray): FormArray {
  const clonedArray: FormArray = new FormArray([]);

  formArray.controls.forEach((control: AbstractControl): void => {
    clonedArray.push(deepCloneFormData(control));
  });

  return clonedArray;
}

/**
 * Recursivly clones a {@type FormControl} object.
 *
 * @param formGroup The form control to be cloned.
 */
function deepCloneFormControl(originalControl: FormControl): FormControl {
  // Set the original value of the FormControl to "defaultValue". This may be different than the current form control value
  // and will be used when resetting the form control. The current value will be set later in the fn.
  const clonedControl: FormControl = new FormControl(originalControl.defaultValue, {
    nonNullable: true,
    updateOn: originalControl.updateOn
  });

  // Copy the current value of the form control. This may be different than the default value.
  clonedControl.setValue(originalControl.value);

  if (hasInternalValidators(originalControl)) {
    clonedControl.setValidators(originalControl._rawValidators as ValidatorFn | Array<ValidatorFn> | null);
    clonedControl.setAsyncValidators(
      originalControl._rawAsyncValidators as AsyncValidatorFn | Array<AsyncValidatorFn> | null
    );
  }

  clonedControl.updateValueAndValidity();

  return clonedControl;
}

/**
 * Type guard to check if a control has internal validators.
 */
function hasInternalValidators(
  control: AbstractControl
): control is AbstractControl & { _rawValidators: unknown; _rawAsyncValidators: unknown } {
  return '_rawValidators' in control && '_rawAsyncValidators' in control;
}
